import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import MasterFilter, { type DateRangeValue, useMasterFilter } from 'components/common/MasterFilter';
import { useAssignableUsers } from 'components/common/MasterFilter/useAssignableUsers';
import PillTabs from 'components/common/PillTabs';
import SubPillTabs from 'components/common/PillTabs/SubPillTabs';
import { useSidebar } from 'contexts/SidebarContext';
import moment from 'moment';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useGetSalesCrmStagesQuery, useGetSalesQuery } from 'services/api/sales.api';
import type { SaleListRow } from 'services/types/sale';
import { MOBILE_PAGE_PT, NAVY, RADIUS_CARD, RADIUS_COIN, SURFACE, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import { INSUREX_BUSINESS_UNIT_ID } from 'utils/constants';

import { sortStages } from './constants';
import salesBarConfig from './salesBarConfig';

const FONT = 'Jost, sans-serif';
const DEFAULT_PAGE_SIZE = 25;

const emptyFilterModel: GridFilterModel = { items: [] };
const emptySortModel: GridSortModel = [];

const toIsoBound = (date: DateRangeValue['start'] | undefined): string | undefined =>
  date?.isValid() ? date.toISOString() : undefined;

/**
 * "Ventas" (Mueve Seguro): every sale already uploaded to Salesforce, grouped by the Etapa and
 * Subetapa the CRM reports back on `Sale.crmState`. Executives only ever receive the sales of
 * their own clients — the backend scopes both the rows and the tab counts.
 */
const Sales: FC = () => {
  const { selectedBusinessUnitId } = useSidebar();
  const businessUnitId = selectedBusinessUnitId ?? 0;
  const isMueveSeguro = businessUnitId === INSUREX_BUSINESS_UNIT_ID;

  const assignableUsers = useAssignableUsers(businessUnitId, { skip: !isMueveSeguro });
  const dynamicOptions = useMemo(() => ({ assignedUser: assignableUsers }), [assignableUsers]);

  const filter = useMasterFilter(salesBarConfig, businessUnitId, dynamicOptions, undefined, {
    urlNamespace: 'sales',
  });
  const uploadedRange = filter.values.uploadedAt as DateRangeValue | undefined;
  const searchTerm = ((filter.values.searchTerm as string) || '').trim();
  const userIds = ((filter.values.assignedUser as string[] | undefined) ?? []).join(',');

  const filterParams = useMemo(
    () => ({
      businessUnitId,
      searchTerm: searchTerm || undefined,
      userIds: userIds || undefined,
      uploadedFrom: toIsoBound(uploadedRange?.start),
      uploadedTo: toIsoBound(uploadedRange?.end),
    }),
    [businessUnitId, searchTerm, userIds, uploadedRange?.start, uploadedRange?.end]
  );

  const { data: stagesData, isLoading: isLoadingStages } = useGetSalesCrmStagesQuery(filterParams, {
    skip: !isMueveSeguro,
  });
  const stages = useMemo(() => sortStages(stagesData?.stages ?? []), [stagesData]);

  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [selectedSubStage, setSelectedSubStage] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });

  // Land on the first Etapa of the funnel, and follow the data if the current one disappears
  // (it can empty out once a filter is applied).
  useEffect(() => {
    if (stages.length === 0) return;
    if (selectedStage && stages.some((stage) => stage.stage === selectedStage)) return;
    setSelectedStage(stages[0].stage);
  }, [stages, selectedStage]);

  useEffect(() => {
    setSelectedSubStage(null);
  }, [selectedStage]);

  // Any change to what the list is narrowed by invalidates the current page offset.
  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [selectedStage, selectedSubStage, filterParams]);

  const { data, isLoading, isFetching } = useGetSalesQuery(
    {
      ...filterParams,
      paginationModel,
      stage: selectedStage ?? undefined,
      subStage: selectedSubStage ?? undefined,
    },
    { skip: !isMueveSeguro || !selectedStage }
  );

  const subStages = stages.find((stage) => stage.stage === selectedStage)?.subStages ?? [];

  // Widths are shares of the container, not pixels — the grid runs with `fitToContainer`.
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'opportunityNumber', headerName: 'Nro Oportunidad', width: 150 },
      {
        field: 'crmUploadedAt',
        headerName: 'Fecha Creación',
        width: 150,
        valueFormatter: (value) => (value ? moment(value as string).format('DD/MM/YYYY HH:mm') : '—'),
      },
      { field: 'clientName', headerName: 'Nombre Cliente', width: 225 },
      { field: 'executiveName', headerName: 'Ejecutivo', width: 190 },
      { field: 'subStage', headerName: 'Subetapa', width: 230 },
      {
        // CustomDataGrid falls back to the field name when headerName is empty, so the action
        // column needs an explicit label.
        field: 'actions',
        headerName: 'Detalle',
        width: 175,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem' }} />}
            // New tab on purpose: the list is a worklist, so the operator keeps their place in it
            // while reviewing a client.
            onClick={() => window.open(`/clients/${(row as SaleListRow).clientId}`, '_blank', 'noopener')}
            sx={{
              textTransform: 'none',
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '0.75rem',
              color: NAVY,
              bgcolor: alpha(NAVY, 0.06),
              borderRadius: RADIUS_COIN,
              px: 1.5,
              py: 0.4,
              boxShadow: 'none',
              '&:hover': { bgcolor: alpha(NAVY, 0.12), boxShadow: 'none' },
            }}
          >
            Ver detalles
          </Button>
        ),
      },
    ],
    []
  );

  if (!isMueveSeguro) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: SURFACE, px: { xs: 2, md: 3 }, pt: 2 }}>
        <Typography sx={{ fontFamily: FONT, color: alpha(NAVY, 0.6) }}>
          Esta vista está disponible solo para Mueve Seguro.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: SURFACE,
        px: { xs: 2, md: 3 },
        pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2 },
        pb: 6,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start', mb: 2 }}>
        {stages.length > 0 && (
          <PillTabs
            tabs={stages.map((stage) => ({ value: stage.stage, label: stage.stage, count: stage.count }))}
            value={selectedStage ?? ''}
            onChange={setSelectedStage}
            ariaLabel="Etapa de la venta en el CRM"
          />
        )}
        {subStages.length > 0 && (
          <SubPillTabs
            tabs={subStages.map((item) => ({ value: item.subStage, label: item.subStage, count: item.count }))}
            value={selectedSubStage}
            onChange={setSelectedSubStage}
          />
        )}
      </Box>

      {isLoadingStages ? (
        <Typography sx={{ fontFamily: FONT, color: alpha(NAVY, 0.6) }}>Cargando ventas...</Typography>
      ) : stages.length === 0 ? (
        <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, p: 4 }}>
          <Typography sx={{ fontFamily: FONT, color: alpha(NAVY, 0.6) }}>
            No hay ventas subidas al CRM con los filtros seleccionados.
          </Typography>
        </Box>
      ) : (
        <CustomDataGrid<SaleListRow>
          rows={data?.sales ?? []}
          columns={columns}
          filterModel={emptyFilterModel}
          sortModel={emptySortModel}
          onFilterModelChange={() => undefined}
          onSortModelChange={() => undefined}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={data?.total ?? 0}
          getRowClassName={() => ''}
          pageId="sales"
          fitToContainer
          disableFilters
          disableSort
          isLoading={isLoading || isFetching}
          noResultsMessage="No hay ventas en esta subetapa"
        />
      )}
    </Box>
  );
};

export default Sales;
